<template>
  <component
    :is="noWrapper ? 'Fragment' : (url ? 'a' : 'div')"
    class="images"
    :class="[
      $blockClass(blockClass, 'image-container'),
      paddingClass,
    ]"
    :href="url"
    :style="{ paddingBottom: aspectRatio }"
  >
    <img
      v-if="image && (image.src || image.url)"
      ref="image"
      class="images__image"
      :class="[
        $blockClass(blockClass, 'image'),
        {
          'lazyload': lazyload && !swiperLazyload,
          'swiper-lazy': lazyload && swiperLazyload,
          'no-animation': noAnimation,
        },
      ]"
      @load="$emit('load')"
      :alt="imageAlt"
      decoding="async"
      :role="role"
      :sizes="getSizes(false)"
      :src="getSrc(false)"
      :srcset="swiperLazyload ? null : getSrcset(false).srcset"
      :fetchpriority="imageHighPriority ? 'high' : null"
      :data-sizes="getSizes(true)"
      :data-src="getSrc(true)"
      :data-srcset="getSrcset(true).srcset"
    >
    <button
      v-if="fullscreen"
      type="button"
      :class="`images__fullscreen-button images__${fullscreenBtnPosition}`"
      title="Expand image"
      @click="$emit('fullscreenToggle')"
      v-html="$icon('misc.expand')"
    />
  </component>
</template>

<script>

/**
 * Vue: Responsive image
 * -----------------------------------------------------------------------------
 * Base template for responsive images.
 * - image.src comes from formatted product response.
 * - image.url comes from unformatted cart.js response.
 *
 * @param {String} [alt] - Override image alt.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {Boolean} [imageHighPriority] - Should the image be downloaded with
 * high priority, defaults to false.
 * @param {Boolean} [fullscreen] - Shows a fullscreen button
 * @param {Strign} [fulllscreenBtnPosition] - Position for the fullscreen button
 *      Options: 'top-left', 'top-right', 'bottom-left', 'bottom-right'
 *      Default: 'bottom-right'
 * @param {Boolean} [forceUpdate] - Force update the image if image prop changes.
 * @param {Object} image - Image object formatted from Shopify response.
 * @param {String} [minMax] - Min and max size for srcset in `min-max` format.
 * @param {Boolean} [noWrapper] - Just render the image tag without any wrapper. Default: false
 * @param {Boolean} [padding] - Add padding based on image ratio or supplied
 * ratio, default to true, disable to make image fill its parent container.
 * @param {String|Boolean} [ratio] - Aspect ratio of image in `width:height`
 * format or in decimal ratio format (width/height), will use original ratio of
 * image if not set or is false.
 * @param {String|Boolean} [url] - Link image to URL.
 * @param {String} [sizes] - Value for sizes attribute.
 * @param {Boolean} [swiperLazyload] - Lazyloaded by Swiper, lazyload must also
 * be true for this to work.
 *
 */
import Fragment from '~global/fragment/fragment'

export default {
  name: 'ResponsiveImage',
  components: {
    Fragment,
  },
  props: {
    alt: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: () => ({}),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    imageHighPriority: {
      type: Boolean,
      default: false,
    },
    forceUpdate: {
      type: Boolean,
      default: false,
    },
    fullscreen: {
      type: Boolean,
      default: false,
    },
    fullscreenBtnPosition: {
      type: String,
      default: 'bottom-right',
      validator(value, props) {
        return [
          'bottom-right', 'bottom-left', 'top-right', 'top-left',
        ].includes(value)
      },
    },
    minMax: {
      type: String,
      default: '200-1000',
    },
    noAnimation: {
      type: Boolean,
      default: false,
    },
    noWrapper: {
      type: Boolean,
      default: false,
    },
    padding: {
      type: Boolean,
      default: true,
    },
    ratio: {
      type: [String, Boolean],
      default: '',
    },
    sizes: {
      type: String,
      default: 'auto',
    },
    swiperLazyload: {
      type: Boolean,
      default: false,
    },
    url: {
      type: [Boolean, String],
      default: false,
    },
  },
  emits: ['fullscreenToggle', 'load'],

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute aspect ratio.
     * @returns {Number|Boolean}
     */
    aspectRatio() {
      if (!this.padding) {
        return false
      }

      /**
       * If ratio is supplied without :, i.e. decimal ratio.
       */
      if (this.ratio && this.ratio.includes('.')) {
        return `${(1 / this.ratio) * 100.00}%`
      }

      /**
       * If no ratio supplied and image has width / height ratio in object.
       */
      if (!this.ratio && this.image.aspect_ratio) {
        return `${(1 / this.image.aspect_ratio) * 100.00}%`
      }

      let height = this.image.height * 100
      let width = this.image.width

      /**
       * If ratio is passed as prop.
       */
      if (this.ratio) {
        height = this.ratio.split(':')[1] * 100.00
        width = this.ratio.split(':')[0]
      }

      return `${height / width}%`
    },

    /**
     * Compute alt.
     * @returns {String}
     */
    imageAlt() {
      const imageAlt = this.alt || this.image.alt
      return imageAlt ? imageAlt : ''
    },

    /**
     * Compute image padding class.
     * @returns {String}
     */
    paddingClass() {
      if (this.padding) {
        return ''
      }

      return 'images--no-padding'
    },

    /**
     * Compute role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },
  },

  mounted() {
    const { image } = this.$refs

    /**
     * Remove lazy loaded classes if component is re-mounted.
     */
    if (image) {
      image.classList.remove('swiper-lazy-loaded')
    }
  },

  methods: {

    /**
     * Get sizes attribute.
     * - Can either be sizes or data-sizes based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getSizes(dataAttribute) {
      if (this.lazyload && !dataAttribute) {
        return null
      }

      return this.sizes
    },

    /**
     * Get src URL.
     * - Can either be src or data-src based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getSrc(dataAttribute) {
      if (
        (!this.image.src && !this.image.url) ||
        (this.lazyload && !this.swiperLazyload && !dataAttribute)
      ) {
        return null
      }

      return this.getSizedImageUrl(
        this.image.src || this.image.url,
        this.getSrcset().placeholder,
      )
    },

    /**
     * Get srcset.
     * - Can either be srcset or data-srcset based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {Object|Null}
     */
    getSrcset(dataAttribute) {
      if (!this.image.src && !this.image.url) {
        return {
          srcset: null,
        }
      }

      const min = Number(this.minMax.split('-')[0])
      const max = Number(this.minMax.split('-')[1])

      const range = max - min
      const increment = range / 5

      const array = [...Array(5)].map((_, index) => {
        const step = increment * (index + 1)
        const value = min + step

        return value
      })

      const srcset = array.map((step) => {
        const roundedSize = Math.ceil(step)

        const sizedImageUrl = this.getSizedImageUrl(
          this.image.src || this.image.url,
          roundedSize,
        )

        return `${sizedImageUrl} ${roundedSize}w`
      })

      return {
        array,
        max,
        min,
        placeholder: (min / 2),
        srcset: this.lazyload && !this.forceUpdate && !dataAttribute ? null : srcset,
      }
    },

    /**
     * Get a sized image from Shopify.
     * - Modified version of getSizedImageUrl from '@shopify/theme-images' pkg.
     * - Formats as image.jpg?width={{size}} instead of image_{{size}}x.jpg
     * to match the img_url liquid filter.
     * - Necessary to prevent images from loading twice as the URLs would be
     * different in liquid and JS.
     * @param src
     * @param size
     * @returns {*}
     */
    getSizedImageUrl(src, size) {
      if (size === null) {
        return src
      }

      if (size === 'master') {
        return this.removeProtocol(src)
      }

      const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i)

      if (match) {
        const prefix = src.split(match[0])
        const suffix = match[0]
        const delimiter = suffix.includes('?') ? '&' : '?'

        return this.removeProtocol(`${prefix[0]}${suffix}${delimiter}width=${size}`)
      } else {
        return null
      }
    },

    /**
     * Remove protocol from a URL.
     * @param {String} path - URL.
     * @returns {String}
     */
    removeProtocol(path) {
      return path.replace(/http(s)?:/, '')
    },
  },
}
</script>

<style lang="scss">
@import './responsive-image';
</style>